import { type RPPDomain } from '@retailer-platform/dashboard/core'
import {
  NavItem,
  AdminNavEntryPoint,
  NavEntryPoint,
  AdminNavEntryPointV2,
} from '@retailer-platform/dashboard/routing'
import { accountManagementRouter } from './routing/accountManagementRouter'
import { accountManagementAdminRouter } from './routing/accountManagementAdminRouter'
import { accountManagementPartnerRouter } from './routing/accountManagementPartnerRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { accountManagementAdminAccessControl } from './access-control/accountManagementAdminAccess.configuration'
import { accountManagementPartnerAccessControl } from './access-control/accountManagementPartnerAccess.configuration'
import { viewRetailerSecurityAccessControl } from './access-control/multiFactorAuthAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'account-management',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'account-management-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [accountManagementRouter],
    admin: [accountManagementAdminRouter],
    partnerScoped: [accountManagementPartnerRouter],
  },
  navigationEntries: [
    {
      attachTo: NavItem.Admin,
      attachToV2: NavEntryPoint.UserMenuUserManagement,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Manage retailer security such as multi-factor authentication',
          route: 'retailer-security',
          labelId: 'accountManagementDomain.nav.security.title',
          accessControl: viewRetailerSecurityAccessControl,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.ManageWarehouseSecurity,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.ManageWarehouseSecurity,
            },
          ],
        },
      ],
    },
    {
      attachTo: NavItem.Admin,
      attachToV2: NavEntryPoint.UserMenuUserManagement,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Manage partner accounts and user permissions for retailers',
          route: 'partner-account-management-list',
          labelId: 'accountManagementDomain.nav.title',
          accessControl: accountManagementPartnerAccessControl,
          positionNavV2: 0,
          subRoutes: ['partner-account-management-create', 'partner-account-management-edit'],
          inToolPermissions: [
            {
              slug: Permission.ImpersonateUser,
              permissionLabelId: 'permissionAccordion.permissions.' + Permission.ImpersonateUser,
            },
          ],
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachTo: AdminNavEntryPoint.Account,
      attachToV2: AdminNavEntryPointV2.UserMenu,
      subNavItems: [
        {
          route: 'app-admin-account-management-list',
          labelId: 'accountManagementDomain.nav.title',
          accessControl: accountManagementAdminAccessControl,
          positionNavV2: 0,
          subRoutes: ['app-admin-account-management-create', 'app-admin-account-management-edit'],
        },
      ],
    },
  ],
}

export default definition
